import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonV2, TextArea, InputV2, TextV2, FormControl, SpacingStack, Flex, DatePickerInput, Divider, useToast } from '@withjoy/joykit';
import { Trash } from '@withjoy/joykit/icons';
import { LocationInputV2 } from '@shared/components/LocationInputV2';
import type { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { AdminSidebarComponentProps } from '../../../JsonComponent.types';
import { WeatherReportContentV103, WeatherReportDaily, WeatherReportItineraryLeg } from '../WeatherReport.types';
import { useTranslation } from '@shared/core/i18n';
import { useWeatherReportEdit } from './WeatherReportEdit.controller';
import { createWeatherReportLegTemplate, deriveLegTitle } from '../WeatherReport.helpers';
import { useFormik } from 'formik';

const EMPTY_LEGS: readonly WeatherReportItineraryLeg[] = [];

export const WeatherReportEdit: React.FC<AdminSidebarComponentProps> = ({ data, eventHandle }) => {
  const { t } = useTranslation('websiteEditor');
  const weatherTrans = t('customPage', 'weatherReport');
  const { toast } = useToast();
  const { initialValues, save, toDate, toIso, mergeDailyWithRange, parseNum, isSaving, lastError } = useWeatherReportEdit({ data, eventHandle });

  const formik = useFormik<WeatherReportContentV103>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      const success = await save(values);
      if (success) {
        toast(weatherTrans.toast.saved());
        helpers.resetForm({ values });
      } else {
        toast(weatherTrans.toast.failed());
      }
      helpers.setSubmitting(false);
    },
  });

  const [activeLegIdx, setActiveLegIdx] = useState<number>(0);
  const legs = formik.values.itinerary ?? EMPTY_LEGS;
  const activeLeg = useMemo<WeatherReportItineraryLeg>(() => legs[activeLegIdx] ?? createWeatherReportLegTemplate(), [legs, activeLegIdx]);

  useEffect(() => {
    if (activeLegIdx >= legs.length && legs.length > 0) {
      setActiveLegIdx(legs.length - 1);
    }
  }, [activeLegIdx, legs.length]);

  const updateLeg = useCallback(
    (idx: number, patch: Partial<WeatherReportItineraryLeg>) => {
      const currentLegs = formik.values.itinerary ?? [];
      const nextLegs = currentLegs.map((leg, legIdx) => {
        if (legIdx !== idx) return leg;
        return {
          ...leg,
          ...patch,
          location: patch.location ? { ...(leg.location || {}), ...patch.location } : leg.location,
          dateRange: patch.dateRange ? { ...(leg.dateRange || {}), ...patch.dateRange } : leg.dateRange,
          daily: patch.daily ?? leg.daily,
        };
      });

      void formik.setFieldValue('itinerary', nextLegs);
    },
    [formik],
  );

  const handleAddLeg = useCallback(() => {
    const currentLegs = formik.values.itinerary ?? [];
    const last = currentLegs[currentLegs.length - 1];
    const baseDate = last?.dateRange?.to ? toDate(last.dateRange.to) : new Date();
    if (baseDate && last?.dateRange?.to) {
      baseDate.setDate(baseDate.getDate() + 1);
    }
    const iso = toIso(baseDate ?? new Date());

    const templateLeg = createWeatherReportLegTemplate({
      label: weatherTrans.cities.fallback({ index: currentLegs.length + 1 }),
      location: { city: '', country: '', name: '', geo: { lat: 0, lon: 0 } },
      dateRange: { from: iso, to: iso },
      guestHighlights: '',
    });

    const normalizedLeg: WeatherReportItineraryLeg = {
      ...templateLeg,
      daily: mergeDailyWithRange([], iso, iso),
    };

    void formik.setFieldValue('itinerary', [...currentLegs, normalizedLeg]);
    setActiveLegIdx(currentLegs.length);
  }, [formik, mergeDailyWithRange, toDate, toIso, weatherTrans]);

  const handleRemoveLeg = useCallback(() => {
    const currentLegs = formik.values.itinerary ?? [];
    if (currentLegs.length <= 1) {
      return;
    }

    const nextLegs = currentLegs.filter((_, idx) => idx !== activeLegIdx);
    void formik.setFieldValue('itinerary', nextLegs);
    setActiveLegIdx(Math.max(0, activeLegIdx - 1));
  }, [activeLegIdx, formik]);

  return (
    <Box padding={4}>
      <form
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <SpacingStack spacing={6}>
          <Box>
            <TextV2 typographyVariant="hed4">{weatherTrans.title()}</TextV2>
            <TextV2
              typographyVariant="label2"
              color="mono12"
            >
              {weatherTrans.subtitle()}
            </TextV2>
          </Box>

          {/* Cities / Itinerary */}
          <Box>
            <TextV2
              typographyVariant="hed6"
              marginBottom={2}
            >
              {weatherTrans.cities.title()}
            </TextV2>
            <Flex
              gap={2}
              flexWrap="wrap"
              marginBottom={4}
            >
              {legs.map((leg, i) => {
                const label = deriveLegTitle(leg, weatherTrans.cities.fallback({ index: i + 1 }));
                const intent = i === activeLegIdx ? 'primary' : 'neutral';
                return (
                  <ButtonV2
                    key={i}
                    size="sm"
                    intent={intent}
                    variant={i === activeLegIdx ? 'solid' : 'outline'}
                    onClick={() => setActiveLegIdx(i)}
                  >
                    {label}
                  </ButtonV2>
                );
              })}
              <ButtonV2
                size="sm"
                variant="ghost"
                onClick={handleAddLeg}
              >
                {weatherTrans.cities.add()}
              </ButtonV2>
            </Flex>

            {legs.length > 1 && (
              <ButtonV2
                size="sm"
                variant="ghost"
                intent="destructive"
                startIcon={<Trash />}
                onClick={handleRemoveLeg}
              >
                {weatherTrans.cities.remove()}
              </ButtonV2>
            )}
          </Box>

          <Divider />

          {/* Location details for active city */}
          <Box>
            <TextV2
              typographyVariant="hed6"
              marginBottom={3}
            >
              {weatherTrans.location.title()}
            </TextV2>
            <SpacingStack spacing={4}>
              <FormControl label={weatherTrans.location.search()}>
                <LocationInputV2
                  name="weatherLocation"
                  includeMapIcon
                  defaultValue={activeLeg.location?.name || [activeLeg.location?.city, activeLeg.location?.country].filter(Boolean).join(', ')}
                  onLocationChange={(loc: LocationInputV2Response | null) => {
                    if (!loc) {
                      updateLeg(activeLegIdx, { location: { ...(activeLeg.location || {}), city: '', country: '', geo: {} } });
                      return;
                    }

                    updateLeg(activeLegIdx, {
                      location: {
                        ...(activeLeg.location || {}),
                        name: activeLeg.location?.name || loc.displayName || activeLeg.location?.city || '',
                        city: loc.city || activeLeg.location?.city,
                        country: loc.country || activeLeg.location?.country,
                        geo: { lat: loc.lat, lon: loc.lng },
                      },
                    });
                  }}
                  allowCustomPlace
                  placePredictionTypes={['(cities)']}
                />
              </FormControl>
              <FormControl label={weatherTrans.location.displayName()}>
                <InputV2
                  placeholder={weatherTrans.location.displayNamePlaceholder()}
                  value={activeLeg.location?.name || ''}
                  onChange={e => updateLeg(activeLegIdx, { location: { ...(activeLeg.location || {}), name: e.target.value } })}
                />
              </FormControl>

              <Flex gap={4}>
                <FormControl
                  width="100%"
                  label={weatherTrans.location.city()}
                >
                  <InputV2
                    value={activeLeg.location?.city || ''}
                    onChange={e => updateLeg(activeLegIdx, { location: { ...(activeLeg.location || {}), city: e.target.value } })}
                  />
                </FormControl>
                <FormControl
                  width="100%"
                  label={weatherTrans.location.country()}
                >
                  <InputV2
                    value={activeLeg.location?.country || ''}
                    onChange={e => updateLeg(activeLegIdx, { location: { ...(activeLeg.location || {}), country: e.target.value } })}
                  />
                </FormControl>
              </Flex>
            </SpacingStack>
          </Box>

          <Divider />

          {/* Date range for active city */}
          <Box>
            <TextV2
              typographyVariant="hed6"
              marginBottom={3}
            >
              {weatherTrans.dates.title()}
            </TextV2>
            <FormControl label={weatherTrans.dates.range()}>
              <DatePickerInput
                isRange
                dateFormat="yyyy-MM-dd"
                mask={null}
                value={[toDate(activeLeg.dateRange?.from || null), toDate(activeLeg.dateRange?.to || null)]}
                onChange={val => {
                  if (Array.isArray(val)) {
                    const [from, to] = val;
                    const nextFrom = toIso(from);
                    const nextTo = toIso(to);
                    const nextDaily = mergeDailyWithRange(activeLeg.daily, nextFrom, nextTo);
                    updateLeg(activeLegIdx, { dateRange: { from: nextFrom, to: nextTo }, daily: nextDaily });
                  }
                }}
              />
            </FormControl>
          </Box>

          <Box>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              marginBottom={3}
            >
              <TextV2 typographyVariant="hed6">{weatherTrans.daily.title()}</TextV2>
              <ButtonV2
                variant="ghost"
                intent="neutral"
                onClick={() => updateLeg(activeLegIdx, { daily: mergeDailyWithRange(activeLeg.daily, activeLeg.dateRange?.from, activeLeg.dateRange?.to) })}
              >
                {weatherTrans.daily.populateFromRange()}
              </ButtonV2>
            </Flex>

            <SpacingStack spacing={4}>
              {(activeLeg.daily || []).map((d, idx) => (
                <Box
                  key={`${d.date}-${idx}`}
                  padding={3}
                  borderRadius={12}
                  backgroundColor="surface"
                  boxShadow="sm"
                >
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom={3}
                  >
                    <FormControl
                      width="60%"
                      label={weatherTrans.daily.date()}
                    >
                      <DatePickerInput
                        dateFormat="yyyy-MM-dd"
                        mask={null}
                        value={toDate(d.date)}
                        onChange={date => {
                          const next = [...(activeLeg.daily || [])];
                          const dt = Array.isArray(date) ? date[0] : (date as Date | null);
                          next[idx] = { ...d, date: toIso(dt) };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                    <ButtonV2
                      variant="ghost"
                      intent="destructive"
                      startIcon={<Trash />}
                      onClick={() => {
                        const next = [...(activeLeg.daily || [])];
                        next.splice(idx, 1);
                        updateLeg(activeLegIdx, { daily: next });
                      }}
                    >
                      {weatherTrans.daily.remove()}
                    </ButtonV2>
                  </Flex>

                  <FormControl label={weatherTrans.daily.summary()}>
                    <InputV2
                      value={d.summary || ''}
                      placeholder={weatherTrans.daily.summaryPlaceholder()}
                      onChange={e => {
                        const next = [...(activeLeg.daily || [])];
                        next[idx] = { ...d, summary: e.target.value };
                        updateLeg(activeLegIdx, { daily: next });
                      }}
                    />
                  </FormControl>

                  <Box __css={{ display: 'grid', gridTemplateColumns: ['1fr 1fr', null, 'repeat(4, 1fr)'], columnGap: 4, rowGap: 4, marginTop: 3 }}>
                    <FormControl label={weatherTrans.daily.highC()}>
                      <InputV2
                        type="number"
                        inputMode="decimal"
                        step="1"
                        value={String(d.highTempC ?? '')}
                        onChange={e => {
                          const next = [...(activeLeg.daily || [])];
                          next[idx] = { ...d, highTempC: parseNum(e.target.value) ?? 0 };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                    <FormControl label={weatherTrans.daily.lowC()}>
                      <InputV2
                        type="number"
                        inputMode="decimal"
                        step="1"
                        value={String(d.lowTempC ?? '')}
                        onChange={e => {
                          const next = [...(activeLeg.daily || [])];
                          next[idx] = { ...d, lowTempC: parseNum(e.target.value) ?? 0 };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                    <FormControl label={weatherTrans.daily.precip01()}>
                      <InputV2
                        type="number"
                        inputMode="decimal"
                        step="0.05"
                        min="0"
                        max="1"
                        value={String(typeof d.precipChance === 'number' ? d.precipChance : '')}
                        onChange={e => {
                          const next = [...(activeLeg.daily || [])];
                          next[idx] = { ...d, precipChance: parseNum(e.target.value) };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                    <FormControl label={weatherTrans.daily.humidityPct()}>
                      <InputV2
                        type="number"
                        inputMode="decimal"
                        step="1"
                        min="0"
                        max="100"
                        value={String(typeof d.humidityPct === 'number' ? d.humidityPct : '')}
                        onChange={e => {
                          const next = [...(activeLeg.daily || [])];
                          next[idx] = { ...d, humidityPct: parseNum(e.target.value) };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                  </Box>

                  <Box __css={{ display: 'grid', gridTemplateColumns: ['1fr 1fr', null, '1fr 1fr'], columnGap: 4, rowGap: 4, marginTop: 3 }}>
                    <FormControl label={weatherTrans.daily.windKph()}>
                      <InputV2
                        type="number"
                        inputMode="decimal"
                        step="1"
                        value={String(typeof d.windSpeedKph === 'number' ? d.windSpeedKph : '')}
                        onChange={e => {
                          const next = [...(activeLeg.daily || [])];
                          next[idx] = { ...d, windSpeedKph: parseNum(e.target.value) };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                    <FormControl label={weatherTrans.daily.uvIndex()}>
                      <InputV2
                        type="number"
                        inputMode="decimal"
                        step="1"
                        min="0"
                        max="11"
                        value={String(typeof d.uvIndex === 'number' ? d.uvIndex : '')}
                        onChange={e => {
                          const next = [...(activeLeg.daily || [])];
                          next[idx] = { ...d, uvIndex: parseNum(e.target.value) };
                          updateLeg(activeLegIdx, { daily: next });
                        }}
                      />
                    </FormControl>
                  </Box>
                </Box>
              ))}

              <ButtonV2
                variant="ghost"
                onClick={() => {
                  const lastDate = activeLeg.daily?.[activeLeg.daily.length - 1]?.date;
                  const base = lastDate ? toDate(lastDate) : null;
                  const nextDate = base ? new Date(base.getTime()) : new Date();
                  if (base) {
                    nextDate.setDate(nextDate.getDate() + 1);
                  }
                  const next: WeatherReportDaily = { date: toIso(nextDate), summary: '', highTempC: 0, lowTempC: 0 };
                  updateLeg(activeLegIdx, { daily: [...(activeLeg.daily || []), next] });
                }}
              >
                + {weatherTrans.daily.addDay()}
              </ButtonV2>
            </SpacingStack>
          </Box>

          <Box>
            <FormControl label={weatherTrans.cityHighlights.label()}>
              <TextArea
                placeholder={weatherTrans.cityHighlights.placeholder()}
                value={activeLeg.guestHighlights || ''}
                onChange={e => updateLeg(activeLegIdx, { guestHighlights: e.target.value })}
                minRows={2}
              />
            </FormControl>
          </Box>

          <Box>
            <FormControl label={weatherTrans.guestHighlights.label()}>
              <TextArea
                placeholder={weatherTrans.guestHighlights.placeholder()}
                value={formik.values.guestHighlights || ''}
                onChange={e => void formik.setFieldValue('guestHighlights', e.target.value)}
                minRows={3}
              />
            </FormControl>
          </Box>

          <Box>
            <ButtonV2
              type="submit"
              intent="primary"
              shape="rounded"
              loading={isSaving || formik.isSubmitting}
            >
              {weatherTrans.saveCta()}
            </ButtonV2>
            {lastError ? (
              <TextV2
                marginTop={2}
                color="danger6"
                typographyVariant="label3"
              >
                {weatherTrans.toast.failed()}
              </TextV2>
            ) : null}
          </Box>
        </SpacingStack>
      </form>
    </Box>
  );
};
